import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ClientEvent, ServerEvent } from '@itr/shared';
import { refreshAccessToken, server, tokens } from './client';

type Handler = (event: ServerEvent) => void;

interface RealtimeContextValue {
  connected: boolean;
  /** When the socket last went down (or the page started without one); null while connected. */
  disconnectedSince: number | null;
  /** When the next reconnect attempt is scheduled, if one is waiting on backoff. */
  nextRetryAt: number | null;
  /** Skip the backoff and try again immediately. */
  reconnectNow: () => void;
  subscribe: (topics: string[]) => () => void;
  on: (handler: Handler) => () => void;
  watchWidget: (instanceId: string, widgetId: string, options: Record<string, unknown>) => () => void;
  send: (event: ClientEvent) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

const HEARTBEAT_MS = 15_000;
/**
 * A socket whose server vanished (host rebooted, proxy dropped it, laptop
 * changed networks) often never fires `close` — it just goes quiet. The server
 * answers every ping, so this much silence means the connection is dead.
 */
const SILENCE_LIMIT_MS = 40_000;

/**
 * WebSocket lifecycle.
 *
 * Reconnects with exponential backoff and re-sends every live subscription on
 * open. Without that replay, a dashboard silently stops updating after a brief
 * network blip and looks fine while showing stale numbers — the worst possible
 * failure mode for a monitoring tool.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const [disconnectedSince, setDisconnectedSince] = useState<number | null>(() => Date.now());
  const [nextRetryAt, setNextRetryAt] = useState<number | null>(null);
  const lastMessageAtRef = useRef(Date.now());
  // `connect` and `dropSocket` need each other; the ref breaks the cycle.
  const dropSocketRef = useRef<(socket: WebSocket) => void>(() => {});
  const socketRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef(new Set<Handler>());
  const topicsRef = useRef(new Map<string, number>());
  const watchesRef = useRef(new Map<string, { widgetId: string; options: Record<string, unknown> }>());
  const attemptRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const closedRef = useRef(false);

  const send = useCallback((event: ClientEvent) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(event));
  }, []);

  const connect = useCallback(() => {
    if (closedRef.current) return;
    const token = tokens.access;
    if (!token) return;

    // Derive the socket origin from the configured API base. Falling back to
    // `location.host` would point at `tauri://localhost` in the desktop shell,
    // where the app is not served by the server at all.
    const base = server.base || location.origin;
    const wsOrigin = base.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    const socket = new WebSocket(`${wsOrigin}/ws?token=${encodeURIComponent(token)}`);
    socketRef.current = socket;

    // Not "connected" until the server's hello: a rejected token still opens
    // the socket before the server closes it, and counting that as up would
    // reset the backoff into a once-a-second retry loop.
    const markConnected = () => {
      setConnected(true);
      setDisconnectedSince(null);
      setNextRetryAt(null);
      attemptRef.current = 0;
    };

    socket.onopen = () => {
      lastMessageAtRef.current = Date.now();
      const topics = [...topicsRef.current.keys()];
      if (topics.length > 0) socket.send(JSON.stringify({ type: 'subscribe', topics }));
      for (const [instanceId, watch] of watchesRef.current) {
        socket.send(JSON.stringify({ type: 'widget.watch', instanceId, ...watch }));
      }
    };

    socket.onmessage = (message) => {
      lastMessageAtRef.current = Date.now();
      try {
        const event = JSON.parse(message.data as string) as ServerEvent;
        if (event.type === 'hello') markConnected();
        // An expired access token (they last a day; an idle tab outlives one).
        // Refresh before the next attempt, or every retry is rejected the same way.
        const raw = event as unknown as { type: string; message?: string };
        if (raw.type === 'error' && raw.message === 'unauthorised') void refreshAccessToken();
        for (const handler of handlersRef.current) handler(event);
      } catch {
        /* ignore malformed frames rather than tearing down the connection */
      }
    };

    socket.onclose = () => dropSocketRef.current(socket);
    socket.onerror = () => dropSocketRef.current(socket);
  }, []);

  /**
   * Tear down one socket and schedule the next attempt. Handlers are detached
   * first so a late `close` from a socket already given up on (a dead one can
   * take minutes to report it) cannot schedule a second reconnect.
   */
  const dropSocket = useCallback(
    (socket: WebSocket) => {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close();
      } catch {
        /* already closed */
      }
      if (socketRef.current !== socket) return;
      socketRef.current = null;
      setConnected(false);
      setDisconnectedSince((since) => since ?? Date.now());
      if (closedRef.current) return;

      // Backoff caps at 20s: fast enough that a brief blip recovers almost
      // instantly, slow enough not to hammer a server that is actually down.
      const delay = Math.min(20_000, 1000 * 2 ** attemptRef.current++);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      setNextRetryAt(Date.now() + delay);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setNextRetryAt(null);
        connect();
      }, delay);
    },
    [connect],
  );
  dropSocketRef.current = dropSocket;

  const reconnectNow = useCallback(() => {
    const state = socketRef.current?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    setNextRetryAt(null);
    attemptRef.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    closedRef.current = false;
    connect();

    // Browsers throttle background tabs; a tab that has been hidden for a while
    // usually comes back to a dead socket that never fired `close`.
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconnectNow();
    };
    document.addEventListener('visibilitychange', onVisible);

    // The browser knows about a lost network long before a socket timeout does.
    const onOffline = () => {
      if (socketRef.current) dropSocket(socketRef.current);
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', reconnectNow);

    const heartbeat = window.setInterval(() => {
      const socket = socketRef.current;
      if (socket?.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastMessageAtRef.current > SILENCE_LIMIT_MS) {
        dropSocket(socket);
        return;
      }
      send({ type: 'ping' });
    }, HEARTBEAT_MS);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', reconnectNow);
      window.clearInterval(heartbeat);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      socketRef.current?.close();
    };
  }, [connect, dropSocket, reconnectNow, send]);

  const subscribe = useCallback(
    (topics: string[]) => {
      const added: string[] = [];
      for (const topic of topics) {
        const count = topicsRef.current.get(topic) ?? 0;
        topicsRef.current.set(topic, count + 1);
        if (count === 0) added.push(topic);
      }
      if (added.length > 0) send({ type: 'subscribe', topics: added });

      return () => {
        const removed: string[] = [];
        for (const topic of topics) {
          const count = (topicsRef.current.get(topic) ?? 1) - 1;
          if (count <= 0) {
            topicsRef.current.delete(topic);
            removed.push(topic);
          } else {
            topicsRef.current.set(topic, count);
          }
        }
        if (removed.length > 0) send({ type: 'unsubscribe', topics: removed });
      };
    },
    [send],
  );

  const on = useCallback((handler: Handler) => {
    handlersRef.current.add(handler);
    return () => handlersRef.current.delete(handler);
  }, []);

  const watchWidget = useCallback(
    (instanceId: string, widgetId: string, options: Record<string, unknown>) => {
      watchesRef.current.set(instanceId, { widgetId, options });
      send({ type: 'widget.watch', instanceId, widgetId, options });
      return () => {
        watchesRef.current.delete(instanceId);
        send({ type: 'widget.unwatch', instanceId });
      };
    },
    [send],
  );

  const value = useMemo(
    () => ({ connected, disconnectedSince, nextRetryAt, reconnectNow, subscribe, on, watchWidget, send }),
    [connected, disconnectedSince, nextRetryAt, reconnectNow, subscribe, on, watchWidget, send],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used inside <RealtimeProvider>');
  return ctx;
}

/**
 * Turns a burst of realtime triggers into at most one `reload` per `ms`: the
 * first runs at once, anything inside the window collapses into one trailing
 * call. For summaries that refetch on events — refetching per frame let an
 * open client page flood the server with its own rollup requests, pinning
 * the event loop until no frame (the map's included) got through at all.
 */
export function useThrottledReload(reload: () => void, ms = 3000): () => void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const lastRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  return useCallback(() => {
    if (timerRef.current !== null) return;
    const wait = lastRef.current + ms - Date.now();
    const fire = () => {
      timerRef.current = null;
      lastRef.current = Date.now();
      reloadRef.current();
    };
    if (wait <= 0) fire();
    else timerRef.current = window.setTimeout(fire, wait);
  }, [ms]);
}

/**
 * True for frames that can move a health rollup: a health *flip*, or a
 * resource appearing, changing or going. A routine reading that leaves the
 * state where it was (the bulk of the traffic) cannot.
 */
export function movesHealthRollup(event: ServerEvent): boolean {
  switch (event.type) {
    case 'resource.updated':
      return event.health !== event.previousHealth;
    case 'resource.created':
    case 'resource.changed':
    case 'resource.deleted':
      return true;
    default:
      return false;
  }
}

/**
 * Subscribe to topics and run a handler for frames. The handler sees every
 * frame on the socket, not only the subscribed topics', so filter on
 * `event.type`.
 */
export function useRealtimeEvent(topics: string[], handler: Handler): void {
  const { subscribe, on } = useRealtime();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const key = topics.join(',');
  useEffect(() => {
    const unsubscribe = subscribe(key.split(',').filter(Boolean));
    const off = on((event) => handlerRef.current(event));
    return () => {
      unsubscribe();
      off();
    };
  }, [key, subscribe, on]);
}
